import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from "react-router-dom"; // ✅ Add this
import * as Sentry from "@sentry/react";
import "./styles/index.scss";
import "./index.css";
import "rc-slider/assets/index.css";
import { GoogleOAuthProvider } from '@react-oauth/google';
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { CurrencyProvider } from "contexts/CurrencyContext";
import { googleClientId, isGoogleOAuthEnabled } from "config/googleOAuth";
// import { initTracker } from "./utils/analytics/tracker";

// Initialize Sentry
Sentry.init({
  dsn: "https://d5132ba7df3211625b7455a22d600af9@o4510657415872512.ingest.us.sentry.io/4510702619394048",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/onlinestore-backend\.innovatorsforge\.com\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  // Enable logs to be sent to Sentry
  enableLogs: true,
});

// Initialize analytics tracker
// const analyticsApiUrl = process.env.REACT_APP_ANALYTICS_API_URL || "http://localhost:3006/api/admin/analytics";
// initTracker({
//   apiUrl: analyticsApiUrl,
//   application: "online_store",
//   enabled: process.env.REACT_APP_ANALYTICS_ENABLED !== "false", // Enabled by default, disable with REACT_APP_ANALYTICS_ENABLED=false
// });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,          // 1 min 
      refetchOnWindowFocus: false,   
    },
  },
});

const sendWebVital = (metric: {
  name: string;
  id: string;
  value: number;
  delta: number;
}) => {
  if (typeof window !== "undefined" && typeof (window as any).gtag === "function") {
    (window as any).gtag("event", metric.name, {
      event_category: "Web Vitals",
      event_label: metric.id,
      value: Math.round(metric.name === "CLS" ? metric.value * 1000 : metric.value),
      metric_value: metric.value,
      metric_delta: metric.delta,
      non_interaction: true,
    });
  }

  if (process.env.NODE_ENV === "development") {
    console.info("[web-vitals]", metric.name, metric.value);
  }
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <QueryClientProvider client={queryClient}>
        <CurrencyProvider>
          {isGoogleOAuthEnabled ? (
            <GoogleOAuthProvider clientId={googleClientId}>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </GoogleOAuthProvider>
          ) : (
            <BrowserRouter>
              <App />
            </BrowserRouter>
          )}
        </CurrencyProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

reportWebVitals(sendWebVital);
